import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Button, ButtonStyle } from 'common/button';
import { Breakpoints } from 'common/enums';
import { InfoLabel } from 'common/info-label';
import { PaginationSelect } from 'common/paginator-select';
import { Spinner } from 'common/spinner';
import { trackCustomEvent } from 'helpers/analytics';
import { dayjs } from 'helpers/dayjs';
import {
  classnames,
  getVaultStatusLabel,
  isCanceledVault,
  isRestrictedVault,
} from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import { Pagination } from 'models/pagination';
import { AccountStateType } from 'pages/account/account-reducer';
import { RouteName, goToPage } from 'routes';
import { AccountVaultsCardsMobile } from './components/account-vaults-cards-mobile';
import { AccountVaultsRows } from './components/account-vaults-rows';
import styles from './subscriptions-table.module.scss';

type SubscriptionsTableProps = {
  accountState: AccountStateType;
  className?: string;
  fetchingVaults: boolean;
  notifyGetMoreVaults: (page: number) => void;
};

const SubscriptionsTable: React.FC<SubscriptionsTableProps> = ({
  accountState,
  className = '',
  fetchingVaults,
  notifyGetMoreVaults,
}) => {
  const mobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);

  const { vaults, pagination } = accountState;

  const isNotLastPage = () => pagination?.lastPage !== 1;

  const showTitle = () => (
    <p className="text__title2__gray800 mb-15"> Subscriptions </p>
  );

  const navigateToVaultCreation = () => {
    goToPage(RouteName.VaultNew);
    trackCustomEvent('startVaultCreation', { step: 'account' });
  };

  const getVaultStatus = (alb: VaultType, positionClass: string) => {
    const { withIcon, statusValue } = getVaultStatusLabel(alb);

    return (
      statusValue && (
        <InfoLabel
          withIcon={withIcon}
          className={classnames(styles.statusLabel, positionClass)}
          content={
            <p
              className={classnames(
                'text__body__semi__bold__overline__gray700',
              )}
            >
              {statusValue}
            </p>
          }
        />
      )
    );
  };
  const getRenewalExpiration = (vault: VaultType) => {
    const isRestricted = isRestrictedVault(vault);

    let postText = '';
    let preText = '';

    if (isRestricted) {
      postText = 'Payment failed.';
    }

    if (isCanceledVault(vault)) {
      preText = 'Cancelled. Expires on:';
    }

    return (
      <span>
        <span>
          <span>
            {preText}
            &nbsp;
          </span>
          <span className={classnames({ [styles.warningText]: isRestricted })}>
            {vault.nextPaymentDate !== null
              ? `${dayjs(vault.nextPaymentDate).format('MM/DD/YY')}`
              : '-'}
          </span>
        </span>
        <span>
          &nbsp;
          {postText}
        </span>
      </span>
    );
  };

  const showMoreVaultsButton = () => (
    <div className={styles.moreVaultsButton}>
      <PaginationSelect
        paginationInfo={pagination as Pagination}
        notifyGetMoreVaults={notifyGetMoreVaults}
      />
    </div>
  );

  const accountRows = () =>
    mobile ? (
      <AccountVaultsCardsMobile
        vaults={vaults}
        getRenewalExpiration={getRenewalExpiration}
        getVaultStatusLabel={getVaultStatus}
      />
    ) : (
      <AccountVaultsRows
        vaults={vaults}
        getRenewalExpiration={getRenewalExpiration}
        getVaultStatusLabel={getVaultStatus}
      />
    );

  const showTable = () => (
    <>
      {fetchingVaults ? (
        <div className={styles.spinnerCenter}>
          <Spinner className={globalStyles.mediumSpinner} />
        </div>
      ) : (
        accountRows()
      )}
      <div className={styles.moreVaultsButton}>
        {accountState.vaults.length && isNotLastPage()
          ? showMoreVaultsButton()
          : ''}
      </div>
    </>
  );

  const showEmptySubscriptions = () => (
    <div className={styles.emptyPage}>
      <div className={styles.emptyPageTexts}>
        <p
          className={classnames('text__heading5__gray700', styles.headingEmpty)}
        >
          You don’t have any active subscriptions
        </p>
        <p className="text__body__regular__medium__gray700">
          Subscriptions belong to vaults you own.
          <span>You can&nbsp;</span>
          <Button
            buttonStyle={ButtonStyle.PrimaryGhostLink}
            onClick={navigateToVaultCreation}
          >
            create a new vault
          </Button>
        </p>
      </div>
    </div>
  );

  return (
    <div className={className}>
      {showTitle()}
      {vaults.length ? showTable() : showEmptySubscriptions()}
    </div>
  );
};

export { SubscriptionsTable };
